import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, timer, of, EMPTY } from 'rxjs';
import { takeUntil, takeWhile, map, switchMap, catchError, tap, finalize, concatMap, filter, take } from 'rxjs/operators';
import { AuthService } from '../../core/auth/auth.service';

@Injectable({
  providedIn: 'root'
})
export class SessionTimerService implements OnDestroy {

  private destroy$ = new Subject<void>();
  private expiresAt: number | null = null; // Use expiresAt to store the actual expiration time
  timeLeft$: Observable<number>;
  private isRefreshing: boolean = false;

  constructor(private authService: AuthService) {
    this.authService.authReady$.pipe(
      take(1), //take only the first emission
      tap(()=>{
          this.startTimer(); // Start the timer when AuthService is ready
      }),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  startTimer() {
    //get expiresAt from authService
    const expiresAtString = this.authService.getExpiresAt();
    if (expiresAtString) {
      this.expiresAt = parseInt(expiresAtString, 10);
      this.timeLeft$ = this.createTimer();
    } else {
      console.error('expires_at não encontrado ou inválido no sessionStorage.');
      this.expiresAt = null;
      this.timeLeft$ = of(0);//return observable of 0
    }
  }

  private createTimer(): Observable<number> {
    return timer(0, 1000).pipe(
      takeUntil(this.destroy$),
      takeWhile(() => this.expiresAt !== null && this.expiresAt > Date.now(), true),//include the last value (0)
      concatMap(() => {
        if(this.expiresAt){
          const timeLeft = this.expiresAt - Date.now();
          if (timeLeft <= 0 && !this.isRefreshing) {
            return this.renovarToken().pipe(
              map(()=>0) // return 0, if success
            );
          }
          return of(Math.max(0, timeLeft)); //return timeLeft
        }
        return of(0); //return 0
      }),
      catchError((error) => {
        console.error('Erro durante a renovação do token:', error);
        this.authService.logout();
        return EMPTY;
      }),
      finalize(() => {
        this.isRefreshing = false;
      })
    );
  }

  renovarToken(): Observable<number> {
    this.isRefreshing = true;
    return this.authService.refreshToken().pipe(
      tap(() => {
        this.startTimer(); //reloads expiration
      }),
      catchError((error) => {
        console.error('Erro ao renovar o token:', error);
        this.authService.logout(); //logout user
        return EMPTY;
      }),
      finalize(() => {
        this.isRefreshing = false;
      })
    );
  }
}
